import { htmlSafe } from '@ember/template';
import { isEmpty } from '@ember/utils';
import moment from 'moment';
import {
  FIRST_HALF,
  SECOND_HALF,
  getProgressionForHalfYear,
} from 'linced-app/utils/progressions-analysis';

/**
 * The progress overview can read a year two ways, and each chart picks one:
 *
 * - HALVES takes each learner's latest judgement for each half of the year,
 *   whatever kind of Milestone it was for. Year-level Milestones are placed
 *   by their order within the year level (see mapMilestonesToHalfYears), as a
 *   mid-year Milestone can fall due after the calendar midpoint; everything
 *   else is placed by its due date.
 * - TYPES takes each learner's latest interim and latest full judgement due
 *   in the year. This suits Milestones counted in weeks or months at school,
 *   which fall due at any point in the year.
 *
 * Either way a year has two slots, kept as `mid` and `end` so the counting
 * code is shared; slotLabels says what they are called in each mode.
 */
export const HALVES = 'halves';
export const TYPES = 'types';

export function slotLabels(mode) {
  if (mode === TYPES) {
    return {
      mid: 'Interim',
      end: 'Full',
    };
  }
  return {
    mid: 'First half',
    end: 'Second half',
  };
}

// What a slot's rule is when it holds nobody to read it from: the first slot
// is interim and the second full, which is always true in TYPES mode and the
// usual case in HALVES mode.
export function defaultIsInterim(half) {
  return half === 'mid';
}

function isInterimMilestone(progression, fallback) {
  let isInterim = progression.get('milestone.isInterim');
  return typeof isInterim === 'boolean' ? isInterim : fallback;
}

function toSlot(progression, isInterim) {
  if (isEmpty(progression)) {
    return null;
  }
  return { judgement: progression.judgement, isInterim };
}

/**
 * resolveSlots picks a learner's two judgements for one academic year.
 *
 * @param {Array} progressions - the learner's progressions, latest first.
 * @param {{startDate, middleDate, endDate}} yearDates
 * @param {string} mode - HALVES or TYPES.
 * @param {Object} halfMap - Milestone ID to half, from
 *   mapMilestonesToHalfYears. Only HALVES uses it.
 * @return {{mid: ?{judgement: number, isInterim: boolean}, end: ?Object}}
 */
export function resolveSlots(progressions, yearDates, mode, halfMap) {
  // A progression without a Milestone has neither a type nor a place in the
  // year's order, so neither mode can read it.
  let judged = progressions.filter(
    (progression) => progression.judgement && progression.get('milestone.id'),
  );
  let dueBetween = (start, end, inclusivity) => (progression) =>
    moment(progression.dueDate).isBetween(start, end, 'day', inclusivity);

  if (mode === TYPES) {
    let inYear = judged.filter(
      dueBetween(yearDates.startDate, yearDates.endDate, '[]'),
    );
    return {
      mid: toSlot(
        inYear.find((progression) => isInterimMilestone(progression, false)),
        true,
      ),
      end: toSlot(
        inYear.find((progression) => !isInterimMilestone(progression, false)),
        false,
      ),
    };
  }

  let mid = getProgressionForHalfYear(judged, halfMap, yearDates, FIRST_HALF);
  let end = getProgressionForHalfYear(judged, halfMap, yearDates, SECOND_HALF);
  return {
    mid: mid ? toSlot(mid, isInterimMilestone(mid, true)) : null,
    end: end ? toSlot(end, isInterimMilestone(end, false)) : null,
  };
}

// thresholdFor is the judgement a slot's learner must reach under their own
// Milestone's rule.
export function thresholdFor(isInterim, grades) {
  return isInterim ? grades.interim : grades.full;
}

export function isOnTrack(slot, grades) {
  return slot.judgement >= thresholdFor(slot.isInterim, grades);
}

// ruleVerb is how being on track reads under each rule.
export function ruleVerb(isInterim) {
  return isInterim ? 'on track' : 'met';
}

/**
 * rulesVerb describes a set of learners judged under one or both rules:
 * "on track", "met", or "on track or met" when they are mixed.
 *
 * @param {{interim: boolean, full: boolean}} rules
 * @param {string} half - which slot, for when the set is empty.
 */
export function rulesVerb(rules, half) {
  if (rules.interim && rules.full) {
    return 'on track or met';
  }
  if (rules.interim || rules.full) {
    return ruleVerb(rules.interim);
  }
  return ruleVerb(defaultIsInterim(half));
}

/**
 * cellStatus says how a count reads against the rules: 'on' when everyone in
 * it is on track, 'off' when nobody is, 'mixed' when some are. An empty cell
 * reads as its level would under the slot's usual rule, or under emptyRule
 * (true for interim, false for full) when the caller knows better.
 */
export function cellStatus(
  count,
  onTrack,
  level,
  half,
  grades,
  emptyRule = defaultIsInterim(half),
) {
  if (!count) {
    return level >= thresholdFor(emptyRule, grades) ? 'on' : 'off';
  }
  if (onTrack === count) {
    return 'on';
  }
  return onTrack ? 'mixed' : 'off';
}

// percentOf is count as a whole-number share of total, or 0 when there is no
// total to share.
export function percentOf(count, total) {
  return total ? Math.round((count / total) * 100) : 0;
}

// formatShare reads a count as "n (p%)", the way every figure on the page
// does; with no total it is just the count.
export function formatShare(count, total) {
  return total ? `${count} (${percentOf(count, total)}%)` : `${count}`;
}

/**
 * barSegments lays out a stacked bar's segments, one per entry in order: each
 * is as wide as its share of total and labelled with that share. Descriptor
 * names are left to the chart's legend and each segment's tooltip. Entries
 * keep their own fields alongside.
 *
 * @param {Array<{count: number, colour: string, textColour: string}>} entries
 * @param {number} total - what the bar's full width stands for.
 * @return {Array<Object>} the entries with share and style added.
 */
export function barSegments(entries, total) {
  return entries.map((entry) => {
    let width = total ? (entry.count / total) * 100 : 0;
    return Object.assign({}, entry, {
      share: formatShare(entry.count, total),
      // Text in black or white, whichever reads on the level's colour.
      style: htmlSafe(
        `width: ${width}%; background-color: ${entry.colour}; color: ${entry.textColour};`,
      ),
    });
  });
}

/**
 * buildCountRows counts learners at each level for both slots of every year,
 * with an on-track total and percentage beneath.
 *
 * @param {Array<Object>} entries - resolved learners ({byYear}).
 * @param {Array<string>} years
 * @param {Array<{key, label, colour, textColour, swatchStyle}>} levels -
 *   highest first.
 * @param {{interim: number, full: number}} grades
 * @return {Array<Object>} the table rows.
 */
export function buildCountRows(entries, years, levels, grades) {
  let emptyYears = (fill) =>
    years.reduce((byYear, year) => {
      byYear[year] = fill();
      return byYear;
    }, {});

  let rows = levels.map((level, index) => ({
    key: level.key,
    label: level.label,
    colour: level.colour,
    textColour: level.textColour,
    swatchStyle: level.swatchStyle,
    isJudgement: true,
    index,
    data: emptyYears(() => ({ mid: 0, end: 0, midOnTrack: 0, endOnTrack: 0 })),
  }));
  let totalRow = {
    key: 'total',
    label: 'On track or met',
    isSummary: true,
    index: levels.length,
    data: emptyYears(() => ({
      midTotal: 0,
      endTotal: 0,
      midOnTrackCount: 0,
      endOnTrackCount: 0,
      midRules: { interim: false, full: false },
      endRules: { interim: false, full: false },
    })),
  };
  let percentageRow = {
    key: 'percentage',
    label: 'Percentage on track',
    isSummary: true,
    isHidden: true,
    index: levels.length + 1,
    data: emptyYears(() => ({ mid: '', end: '' })),
  };

  entries.forEach((entry) => {
    years.forEach((year) => {
      ['mid', 'end'].forEach((half) => {
        let slot = entry.byYear[year][half];
        if (isEmpty(slot)) {
          return;
        }
        let row = rows.find((candidate) => candidate.key === slot.judgement);
        let total = totalRow.data[year];
        let onTrack = isOnTrack(slot, grades);
        if (row) {
          row.data[year][half]++;
          if (onTrack) {
            row.data[year][`${half}OnTrack`]++;
          }
        }
        total[`${half}Total`]++;
        total[`${half}Rules`][slot.isInterim ? 'interim' : 'full'] = true;
        if (onTrack) {
          total[`${half}OnTrackCount`]++;
        }
      });
    });
  });

  years.forEach((year) => {
    let total = totalRow.data[year];
    ['mid', 'end'].forEach((half) => {
      let count = total[`${half}Total`];
      let onTrack = total[`${half}OnTrackCount`];
      // A slot nobody was judged in reads as "—" throughout, as the strand
      // tables show it, rather than a column of zeros out of nobody.
      rows.forEach((row) => {
        let cell = row.data[year];
        cell[`${half}Label`] = count ? formatShare(cell[half], count) : '—';
        cell[`${half}Status`] = count
          ? cellStatus(
              cell[half],
              cell[`${half}OnTrack`],
              row.key,
              half,
              grades,
            )
          : 'empty';
      });
      total[half] = count
        ? `${onTrack} of ${count} (${percentOf(onTrack, count)}%)`
        : '—';
      percentageRow.data[year][half] = count ? percentOf(onTrack, count) : '';
    });
  });

  return [...rows, totalRow, percentageRow];
}

/**
 * mixedColumns flags the slots whose judgements were made under both rules,
 * which only happens when a half year holds interim and full Milestones.
 *
 * @param {Array<Object>} rows - as built by buildCountRows.
 * @param {Array<string>} years
 * @return {Object} {[year]: {mid, end}, any}
 */
export function mixedColumns(rows, years) {
  let total = rows.find((row) => row.key === 'total');
  let mixed = { any: false };
  years.forEach((year) => {
    let cell = total.data[year];
    mixed[year] = {
      mid: cell.midRules.interim && cell.midRules.full,
      end: cell.endRules.interim && cell.endRules.full,
    };
    mixed.any = mixed.any || mixed[year].mid || mixed[year].end;
  });
  return mixed;
}

// Cohort flow chart helpers.

/**
 * milestoneOptions lists both slots of every year the page covers, oldest
 * first, as the points a cohort's movement can be measured between.
 *
 * @param {Array<string>} years - academic year labels, oldest first.
 * @param {{mid: string, end: string}} labels - what the two slots are called
 *   in the chart's mode.
 * @return {Array<{key: string, value: string, year: string, half: string, label: string}>}
 */
export function milestoneOptions(years, labels) {
  return (years || []).reduce((options, year) => {
    ['mid', 'end'].forEach((half) => {
      let key = `${year}:${half}`;
      options.push({
        key,
        // value is what select menus match on.
        value: key,
        year,
        half,
        label: `${labels[half]} ${year}`,
      });
    });
    return options;
  }, []);
}

/**
 * slotAt reads a learner's resolved judgement for one slot of a year, or null
 * when there is none.
 */
export function slotAt(entry, year, half) {
  let byYear = entry.byYear[year];
  let slot = byYear ? byYear[half] : null;
  if (isEmpty(slot) || !slot.judgement) {
    return null;
  }
  return slot;
}

/**
 * flowPairs pairs each learner's judgement at one milestone with their
 * judgement at another. Learners without a judgement at both are left out:
 * there is no movement to show for them.
 *
 * @param {Array<Object>} entries - resolved learner judgements.
 * @param {{year: string, half: string}} from
 * @param {{year: string, half: string}} to
 * @return {Array<{entry: Object, from: number, to: number, fromSlot: Object, toSlot: Object}>}
 */
export function flowPairs(entries, from, to) {
  return (entries || [])
    .map((entry) => {
      let fromSlot = slotAt(entry, from.year, from.half);
      let toSlot = slotAt(entry, to.year, to.half);
      return {
        entry,
        fromSlot,
        toSlot,
        from: fromSlot ? fromSlot.judgement : null,
        to: toSlot ? toSlot.judgement : null,
      };
    })
    .filter((pair) => pair.from && pair.to);
}

// movementFor describes how a learner's descriptor changed between the two
// milestones. Holding a descriptor is progress, since the content moves on.
export function movementFor(from, to) {
  if (to > from) {
    return 'moved up';
  }
  if (to < from) {
    return 'moved down';
  }
  return 'held';
}
